// ADR 6.9.26c §1 — the emit half of the telemetry seam.
//
// This module holds the two SDK handles and every call that reads them, so no
// call site anywhere else in `web/` branches on consent: `capture()` is a
// no-op when PostHog was never constructed, and `captureException()` is a
// no-op when Sentry was never constructed. That is the whole reason the gate
// can be strict — a denied visitor changes nothing about the code that runs.
//
// `lifecycle.ts` constructs and attaches (30.9.26i moved it out of `index.ts`);
// this module is attached *to*. The dependency runs one way (lifecycle →
// capture) so the dynamic `import()`s stay in
// one file and nothing here can pull an SDK into the entry chunk.

/**
 * The slice of `posthog-js` this app uses. A structural type rather than the
 * SDK's own: importing the type would be a value-free import today and a
 * static dependency the moment someone widens it, which is the mistake §4's
 * bundle budget exists to catch one step later.
 */
export interface PostHogLike {
  capture(event: string, properties?: Record<string, unknown>): void
  opt_out_capturing?(): void
  // ADR 30.9.26i §2.5 — PostHog's own persisted opt-out, read and cleared at
  // construction so it never outvotes the web's consent record.
  has_opted_out_capturing?(): boolean
  clear_opt_in_out_capturing?(): void
}

/**
 * The slice of `@sentry/react` this app uses.
 *
 * The second parameter is `captureContext`, and ADR 6.9.26n §4 is what needs
 * it: a parse failure has no stack trace worth grouping on, so the report is
 * only useful with an **explicit fingerprint** — the same thing
 * `TutorStreamParseReporting.kt` reaches for on Android.
 */
export interface SentryLike {
  captureException(
    error: unknown,
    context?: {
      fingerprint?: string[]
      tags?: Record<string, string>
      // ADR 8.10.26k §1 — `reportNonFatal`'s structured evidence; never set by `captureException`.
      contexts?: Record<string, Record<string, unknown>>
      // ADR 15.10.26d §0 — `reportCaught`'s `info` records; never set by the other two.
      level?: 'info' | 'warning'
    },
  ): void
  // ADR 30.9.26i §2.2 — what a withdrawal needs to stop Sentry's *global*
  // handlers, not only our handle. Optional: the suites' fakes predate them.
  getClient?(): { getOptions(): { enabled?: boolean } } | undefined
  close?(timeout?: number): PromiseLike<boolean>
}

let posthog: PostHogLike | null = null
let sentry: SentryLike | null = null

export function attachPostHog(instance: PostHogLike): void {
  posthog = instance
}

export function attachSentry(instance: SentryLike): void {
  sentry = instance
}

/**
 * ADR 30.9.26i §2.2 — withdraw analytics: PostHog's own opt-out, then drop the
 * handle. Detaching is deliberately *not* un-constructing — an SDK already
 * constructed cannot be within the page — but it stops every later event.
 */
export function detachAnalytics(): void {
  posthog?.opt_out_capturing?.()
  posthog = null
}

/**
 * ADR 30.9.26i §2.2 — withdraw crash reports, and actually stop Sentry.
 *
 * Dropping our handle alone left `sentry.init`'s global error handlers
 * reporting uncaught errors. The order is load-bearing: `close()` awaits a
 * flush and only then disables the client, so the live options object is
 * switched off *first* (mutated, never copied — `getOptions()` returns the
 * client's own in `@sentry/core` 10.x), the handle is dropped, and `close()`
 * then removes the integrations, not awaited.
 */
export function detachCrashReports(): void {
  const instance = sentry
  sentry = null
  if (instance === null) return
  try {
    const options = instance.getClient?.()?.getOptions()
    if (options) options.enabled = false
    const closing = instance.close?.()
    if (closing) Promise.resolve(closing).catch((error: unknown) => console.warn('telemetry teardown failed', error))
  } catch (error) {
    console.warn('telemetry teardown failed', error)
  }
}

/**
 * Drop both handles — the teardown every suite's harness imports, kept as the
 * sum of the two per-SDK detaches (30.9.26i §2.2) so none of them changes.
 */
export function detachTelemetry(): void {
  detachAnalytics()
  detachCrashReports()
}

/** Test seam: what is currently attached, without exporting the handles. */
export function attached(): { analytics: boolean; crashReports: boolean } {
  return { analytics: posthog !== null, crashReports: sentry !== null }
}

/**
 * The property every event carries in addition to the SDK's own `$lib`.
 *
 * `$lib` is `posthog-js`'s and an SDK swap would move it; `client` is ours and
 * will not. It is also the forward-compatible discriminator: once the native
 * clients emit it too, a breakdown can move off `$lib` onto this (§1).
 */
export const CLIENT = 'web'

/** Capture an event, stamped with `client`. A no-op with no PostHog instance. */
export function capture(event: string, properties: Record<string, unknown> = {}): void {
  posthog?.capture(event, { ...properties, client: CLIENT })
}

/**
 * Report an exception. A no-op with no Sentry instance — §2 relies on that.
 *
 * `tags` is ADR 9.9.26b §2: two boundaries call through here and Sentry groups
 * by stack, so without a mark a route that threw and a provider that threw are
 * one population in the issue list. Tags only — no `fingerprint`, no `contexts`:
 * `reportNonFatal` already owns grouping, and two functions that both take a
 * fingerprint would be a question at every call site.
 *
 * Amended by ADR 15.10.26d §0: `reportCaught` is the one place a *real* error
 * travels with a fingerprint and contexts. The rule stands for this function,
 * which stays tags-only for the two app boundaries; the three are told apart
 * by what they carry, so a call site still has one answer.
 */
export function captureException(error: unknown, tags?: Record<string, string>): void {
  // Branching rather than `captureException(error, tags ? { tags } : undefined)`.
  // The one-liner calls the SDK with *two* arguments on every path, `undefined`
  // included, and vitest's `toHaveBeenCalledWith` compares the arguments array
  // by length — so two suites that assert the bare shape would go red on a
  // change that altered no behaviour at all. This keeps the untagged wire
  // byte-identical rather than merely equivalent.
  if (tags) {
    sentry?.captureException(error, { tags })
    return
  }
  sentry?.captureException(error)
}

/**
 * A named, explicitly-grouped non-fatal (ADR 6.9.26n §4).
 *
 * The phones' rail, ported: a defect goes to Sentry behind
 * `crash_reports_opt_out`, not to PostHog behind `analytics_opt_out`, and what
 * a contract bug needs is **grouping** — which a free-text property on an
 * analytics event cannot give at all. `fingerprint` is therefore not optional
 * and not decoration: without it Sentry's default grouping fragments one bug
 * across every byte offset it happened to carry.
 *
 * The error's `name` is the fingerprint's first element so the issue title
 * reads as the failure class, which is the job `TutorStreamParseException`
 * does on Android.
 */
export function reportNonFatal(
  message: string,
  fingerprint: string[],
  tags: Record<string, string> = {},
  contexts?: Record<string, Record<string, unknown>>,
): void {
  const error = new Error(message)
  error.name = fingerprint[0] ?? 'non-fatal'
  // ADR 8.10.26k §1 — no `contexts` key at all when none is given, so the
  // existing wire is unchanged rather than merely equivalent.
  sentry?.captureException(error, contexts ? { fingerprint, tags, contexts } : { fingerprint, tags })
}

/**
 * ADR 15.10.26d §0 — a caught error with its own stack, explicitly grouped,
 * with structured evidence. Unlike `reportNonFatal` the error is the caller's
 * (a render throw, a rejected `prompt()`), so its stack is the browser's; unlike
 * `captureException` it carries a fingerprint and contexts (9.9.26b §2 amended).
 * A no-op with no Sentry instance.
 */
export function reportCaught(
  error: unknown,
  report: {
    fingerprint: string[]
    tags: Record<string, string>
    contexts: Record<string, Record<string, unknown>>
    level?: 'info' | 'warning'
  },
): void {
  sentry?.captureException(error, report)
}
